// ============================================================
// Labels — keep in sync with Backend/celesta/labels.py
// ============================================================

export const LABELS = {
  relation: ["Activation", "Inhibition", "Phosphorylation"],
  entity_type: ["protein", "gene", "chemical", "mRNA", "complex", "biological_process", "other"],
  location: ["Cytoplasm", "Cell Membrane", "Cell Nucleus", "Extracellular Matrix", "Extracellular Space"],
  cell_line: ["HeLa cell", "MCF7 cell", "293 cell", "A549 cell", "NIH-3T3 cell", "Hep G2 cell", "U-937 cell", "THP-1 cell", "LNCAP cell", "COS-1 cell"],
  cell_type: ["macrophage", "endothelial cell", "fibroblast", "monocyte", "fat cell", "smooth muscle cell", "hepatocyte", "T cell", "cardiac muscle cell", "neuron"],
  organ: ["liver", "lung", "skeletal muscle tissue", "endothelium", "cardiovascular system", "adipose tissue", "heart", "cardiovascular system endothelium", "blood vessel smooth muscle", "aorta"],
  disease: ["neuroblastoma", "breast cancer", "lung cancer", "atherosclerosis", "multiple myeloma", "leukemia", "melanoma", "osteosarcoma", "non-small cell lung carcinoma"],
  species: ["Homo sapiens", "Mus musculus", "Rattus norvegicus"],
};

export const CONTEXT_CATEGORIES = ["location", "cell_line", "cell_type", "organ", "disease", "species"];

// ============================================================
// Colors
// ============================================================

export const COLORS = {
  regulator:  { bg: "#DBEEFF", text: "#1565C0", border: "#90CAF9" },
  regulated:  { bg: "#FFF8E1", text: "#F57F17", border: "#FFD54F" },
  relation:   { text: "#003594" },
  location:   { bg: "#E8F5E9", text: "#2E7D32", border: "#A5D6A7" },
  cell_line:  { bg: "#F3E5F5", text: "#7B1FA2", border: "#CE93D8" },
  cell_type:  { bg: "#E0F7FA", text: "#00838F", border: "#80DEEA" },
  organ:      { bg: "#FFF3E0", text: "#E65100", border: "#FFCC80" },
  disease:    { bg: "#FFEBEE", text: "#C62828", border: "#EF9A9A" },
  species:    { bg: "#EFEBE9", text: "#4E342E", border: "#BCAAA4" },
};

// ============================================================
// API — everything goes through the backend
// ============================================================

const API_BASE = "/api";

// Split Quick-mode paragraph input into sentence-sized annotation units.
// Intl.Segmenter handles biomedical punctuation such as decimals and common
// abbreviations more reliably than splitting on every period.
export function splitParagraphIntoSentences(text) {
  const normalized = String(text || "")
    .replace(/\r\n?/g, "\n")
    .trim();

  if (!normalized) return [];

  if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
    const segmenter = new Intl.Segmenter("en", { granularity: "sentence" });
    return Array.from(segmenter.segment(normalized), ({ segment }) =>
      segment.trim(),
    ).filter(Boolean);
  }

  // Compatibility fallback for browsers without Intl.Segmenter. Newlines are
  // always boundaries; punctuation is a boundary when the next sentence looks
  // like it starts with an uppercase letter or number.
  const sentences = [];
  let start = 0;
  const pushSentence = (end) => {
    const sentence = normalized.slice(start, end).trim();
    if (sentence) sentences.push(sentence);
  };

  for (let i = 0; i < normalized.length; i += 1) {
    if (normalized[i] === "\n") {
      pushSentence(i);
      while (normalized[i + 1] === "\n") i += 1;
      start = i + 1;
      continue;
    }

    if (!".!?".includes(normalized[i])) continue;

    // Do not split decimal numbers (for example, 2.5 mg).
    if (
      normalized[i] === "." &&
      /\d/.test(normalized[i - 1] || "") &&
      /\d/.test(normalized[i + 1] || "")
    ) {
      continue;
    }

    let punctuationEnd = i + 1;
    while (
      punctuationEnd < normalized.length &&
      ".!?\"')]}".includes(normalized[punctuationEnd])
    ) {
      punctuationEnd += 1;
    }

    let next = punctuationEnd;
    while (next < normalized.length && /[ \t]/.test(normalized[next])) {
      next += 1;
    }

    const nextChar = normalized[next];
    const looksLikeSentenceStart =
      /[A-Z0-9]/.test(nextChar || "") || "\"'([".includes(nextChar || "");
    const isBoundary =
      next >= normalized.length ||
      normalized[next] === "\n" ||
      looksLikeSentenceStart;

    if (isBoundary) {
      pushSentence(punctuationEnd);
      start = next;
      i = next - 1;
    }
  }

  pushSentence(normalized.length);
  return sentences;
}

export async function annotateText(text) {
  try {
    const res = await fetch(`${API_BASE}/celesta/annotate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
    if (res.ok) {
      const data = await res.json();
      return data.result || data;
    }
    console.error("Backend error:", res.status);
  } catch (err) {
    console.error("Backend unreachable:", err);
  }
  return { entities: [], relation: null, contexts: [] };
}

export async function annotateTexts(items) {
  const out = [];
  for (const item of items) out.push(await annotateText(item.text));
  return out;
}
